import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { Button, ComponentSync, EditableDBItemV3, LL_H_C, LL_V_L, SimpleListAdapter, TS_DropDown, TS_Input, TS_MultiSelect_V2 } from '@nu-art/thunderstorm-frontend/index';
import { DBDef_Action, DBDef_OrderGroup } from '@app/pathway-shared';
import { exists, filterDuplicates, filterInstances, ImplementationMissingException, MUSTNeverHappenException, removeItemFromArray, sortArray, voidFunction } from '@nu-art/ts-common';
import { dispatch_PathwayStateRealtimeChanges } from '../../../dispatchers.js';
import { ICONS } from '@app/styles';
import { Empty_PathwayTransition } from '../../../consts.js';
import { Header_Pathway } from '../../../headers/Header_Pathway.js';
import { Header_PathwayTransition } from '../../../headers/Header_PathwayTransition.js';
import { Dialog_Confirmation, QuaiManager_DropDownCaret } from '@app/styles';
import { ModuleFE_Action, ModuleFE_OrderGroup, ModuleFE_Pathway, ModuleFE_PathwayState, ModuleFE_PathwayTransition } from '../../../../../_entity.js';
import { createTransitionLabel } from '@app/pathway-shared';
import { basicSyncEventRefresh, updateResolutionFunctionTitle, updateTransitionOrderExpression } from '../../../utils.js';
import { getCleanMetaPathwayAction, isMetaPathwayAction_WithProtocol, metaPathwayAction_Type_LabelMap } from '@app/pathway-shared';
import { Dropdown_MetaPathwayAction_Type } from '../../../../../_enum.js';
import { DropDown_Pathway } from '../../../../../_entity/pathway/ui-components.js';
import { DropDown_Precondition } from '../../../../../_entity/pathway-state/ui-components.js';
import { ModuleFE_PathwayEditor } from '../../../../../modules/index.js';
import { Renderer_PathwayItemChildSelector } from '../../../renderers/Renderer_PathwayItemChildSelector.js';
import { ModuleFE_Expression } from '@app/hcs-frontend';
import { Renderer_OrderExpressionIndicator } from '../../../renderers/Renderer_OrderExpressionIndicator.js';
export class Renderer_PathwayTransitionEditor extends ComponentSync {
    __onPathwayTransitionUpdated(...params) {
        const pathwayTransitionId = this.state.pathwayTransition.get('_id');
        if (!pathwayTransitionId)
            return;
        basicSyncEventRefresh(pathwayTransitionId, params, () => this.props.functions.selectPathwayTransition(pathwayTransitionId, true), () => this.props.functions.deselectPathwayTransition());
    }
    // ######################## Lifecycle ########################
    deriveStateFromProps(nextProps, state) {
        state.pathwayTransition = nextProps.pathwayTransition;
        return state;
    }
    // ######################## Logic ########################
    getTargetStates = () => {
        const pathwayId = ModuleFE_PathwayEditor.getViewedPathwayId();
        const originId = this.state.pathwayTransition.item.sourceId;
        return ModuleFE_PathwayState.cache.filter(ps => ps.pathwayIds.includes(pathwayId) && ps._id !== originId);
    };
    canSave = () => {
        const pathwayTransition = this.state.pathwayTransition.item;
        return !!pathwayTransition.targetId;
    };
    createDefaultLabel = (transition) => {
        const targetStateId = transition.get('targetId');
        const targetState = ModuleFE_PathwayState.cache.unique(targetStateId);
        if (!targetState)
            throw new MUSTNeverHappenException(`Pathway state with id ${targetStateId} not found`);
        const sameTargetTransition = this.getSameSourceAndTargetStateLabels(transition);
        return createTransitionLabel(sameTargetTransition, targetState);
    };
    getSameSourceAndTargetStateLabels = (transition) => {
        const targetStateId = transition.get('targetId');
        const sourceStateId = transition.get('sourceId');
        return filterInstances(ModuleFE_PathwayTransition.cache.filter(_transition => {
            if (_transition._id === transition.get('_id'))
                return false;
            return _transition.targetId === targetStateId && _transition.sourceId === sourceStateId;
        }).map(_transition => _transition.label));
    };
    saveImpl = async (createNew, detachTransition = false) => {
        // deselect old state/transition to avoid graph rendering issues
        this.props.functions.deselectPathwayTransition();
        const transition = this.state.pathwayTransition;
        let transitionLabel = transition.get('label');
        // Get other labels to check if a label index reset is needed
        const sameSourceAndTargetStateLabels = this.getSameSourceAndTargetStateLabels(transition);
        // Reset or create label if needed
        if ((!transitionLabel || !transitionLabel.length) || sameSourceAndTargetStateLabels.includes(transitionLabel.trim()))
            transitionLabel = this.createDefaultLabel(transition);
        // Add new transition last in priority
        if (!exists(transition.get('priorityIndex'))) {
            const stateTransitions = ModuleFE_PathwayTransition.cache.filter(_transition => _transition.sourceId === transition.get('sourceId'));
            transition.set('priorityIndex', stateTransitions.length);
        }
        try {
            //No problem to assume properties on transition exist because we wouldn't get to this line if they didn't
            const pathwayId = ModuleFE_PathwayEditor.getViewedPathwayId();
            if (!pathwayId)
                throw new MUSTNeverHappenException('cannot proceed without pathway context');
            let _transition;
            if (detachTransition) {
                const resolutionFunction = ModuleFE_Expression.cache.unique(transition.item.resolutionFunctionId);
                const result = await ModuleFE_PathwayTransition._v1.detachTransitionContext({
                    pathwayTransition: transition.item,
                    resolutionFunction,
                    pathwayId
                }).executeSync();
                _transition = result.pathwayTransitions.updated[0];
            }
            else
                _transition = await transition.save();
            // update resolution function label
            if (_transition.resolutionFunctionId)
                await updateResolutionFunctionTitle(_transition._id, [_transition.sourceId, _transition.targetId]);
            //Post save logic
            if (!createNew)
                return this.props.functions.selectPathwayTransition(_transition._id);
            const newTransition = Empty_PathwayTransition(pathwayId, _transition.sourceId, transitionLabel);
            this.props.functions.selectPathwayTransition(new EditableDBItemV3(newTransition, ModuleFE_PathwayTransition));
        }
        catch (e) {
            this.logError(e);
        }
    };
    onSave = async (createNew = false) => {
        if (!this.canSave())
            return;
        const pathwayId = ModuleFE_PathwayEditor.getViewedPathwayId();
        if (!pathwayId)
            throw new MUSTNeverHappenException('cannot render this page without pathway context');
        const pathway = ModuleFE_Pathway.cache.unique(pathwayId);
        if (!pathway)
            throw new MUSTNeverHappenException(`cannot find pathway with id ${pathwayId}`);
        // This is not a child pathway, or a new state, or not belonged to the pathway parent
        if (!pathway._parentId || !this.state.pathwayTransition.item._id || !this.state.pathwayTransition.item.pathwayIds?.includes(pathway._parentId))
            return this.saveImpl(createNew);
        return Dialog_Confirmation.show({
            content: _jsx(_Fragment, { children: _jsx("div", { children: "By editing this transition, the transition and its resolution function will be detached from the parent and won't receive more updates" }) }),
            buttons: [
                {
                    label: 'Yes, Edit',
                    isAsync: true,
                    action: () => this.saveImpl(createNew, true)
                },
                {
                    label: 'Cancel', action: voidFunction, isPreferred: true
                }
            ]
        });
    };
    onCancel = () => {
        const transitionId = this.state.pathwayTransition.get('_id');
        if (!transitionId)
            return this.props.functions.deselectPathwayTransition();
        this.props.toggleEdit();
        // Re-select the transition to revert changes, can assume existence because of the condition above
        this.props.functions.selectPathwayTransition(transitionId);
    };
    // ######################## Render ########################
    render() {
        return _jsxs(LL_V_L, { className: 'match_parent', style: { padding: '30px', gap: '10px' }, children: [this.renderHeader(), this.renderForm(), this.renderButtons()] });
    }
    renderButtons = () => {
        return _jsxs(LL_H_C, { className: 'pathway__bottom-buttons', children: [_jsx(Button, { variant: 'secondary', onClick: this.onCancel, children: "Cancel" }), _jsx(Button, { variant: 'primary', onClick: () => this.onSave(true), disabled: !this.canSave(), children: "Add Another Transition" }), _jsx(Button, { variant: 'primary', onClick: () => this.onSave(), className: 'preferred-action', disabled: !this.canSave(), children: "Save" })] });
    };
    // ######################## Render - Header ########################
    renderHeader = () => {
        const pathwayId = ModuleFE_PathwayEditor.getViewedPathwayId();
        const pathway = ModuleFE_Pathway.cache.unique(pathwayId);
        if (!pathway)
            throw new MUSTNeverHappenException(`Could not get pathway for id ${pathwayId}`);
        return _jsxs(_Fragment, { children: [_jsx(Header_Pathway, { pathway: new EditableDBItemV3(pathway, ModuleFE_Pathway), allowActions: false, functions: this.props.functions }), _jsx(Header_PathwayTransition, { pathwayTransition: this.state.pathwayTransition, functions: this.props.functions })] });
    };
    // ######################## Render - Form ########################
    renderForm = () => {
        return _jsxs(LL_V_L, { className: 'pathway__form', children: [this.renderTargetDropdown(), this.renderLabelInput(), this.childrenSelector(), this.renderAlertInput(), this.renderTests(), this.renderTreatments(), this.renderConsult(), this.renderNurse(), this.renderMetapathwayActions()] });
    };
    renderOrdersMultiSelect = (prop, subType) => {
        return _jsx(TS_MultiSelect_V2, { editable: this.state.pathwayTransition, prop: prop, itemsDirection: 'vertical', selectionRenderer: selector => {
                return _jsx(DropDown_Precondition.selectable, { canUnselect: false, placeholder: 'Add an action', caret: QuaiManager_DropDownCaret, queryFilter: item => {
                        if (selector.existingItems.findIndex(pointer => pointer.id === item.item._id) !== -1)
                            return false;
                        switch (item.dbKey) {
                            case DBDef_OrderGroup.dbKey: {
                                const orderGroup = item.item;
                                return orderGroup.subType === subType;
                            }
                            case DBDef_Action.dbKey: {
                                const action = item.item;
                                return ModuleFE_Action.is.order(action) && action.subType === subType;
                            }
                        }
                    }, onSelected: pointer => {
                        selector.onSelected({ dbKey: pointer.dbKey, id: pointer.item._id });
                        this.forceUpdate();
                    } });
            }, itemRenderer: (pointer, onDelete, disabled) => {
                switch (pointer.dbKey) {
                    case DBDef_Action.dbKey: {
                        const order = ModuleFE_Action.cache.unique(pointer.id);
                        if (!order)
                            throw new MUSTNeverHappenException(`Could not get order for id ${pointer.id}`);
                        return _jsxs(_Fragment, { children: [order.name, _jsx(Renderer_OrderExpressionIndicator, { orderId: order._id, editMode: true, expressionId: pointer.expressionId, onExpressionChange: async (expressionId) => {
                                        await updateTransitionOrderExpression(this.state.pathwayTransition, prop, pointer.id, expressionId);
                                        this.forceUpdate();
                                    } }), !disabled && _jsx(ICONS.x.component, { onClick: onDelete })] });
                    }
                    case DBDef_OrderGroup.dbKey: {
                        const group = ModuleFE_OrderGroup.cache.unique(pointer.id);
                        if (!group)
                            throw new MUSTNeverHappenException(`Could not get order group for id ${pointer.id}`);
                        return _jsxs(_Fragment, { children: [group.name, " ", !disabled && _jsx(ICONS.x.component, { onClick: onDelete })] });
                    }
                }
            }, sort: (items) => sortArray(items, item => {
                switch (item.dbKey) {
                    case DBDef_Action.dbKey:
                        return ModuleFE_Action.cache.unique(item.id)?.name ?? 'N/A';
                    case DBDef_OrderGroup.dbKey:
                        return ModuleFE_OrderGroup.cache.unique(item.id)?.name ?? 'N/A';
                    default: {
                        throw new ImplementationMissingException(`Sort handling for dbKey ${item.dbKey} missing!`);
                    }
                }
            }) });
    };
    renderTargetDropdown = () => {
        const pathwayTransition = this.state.pathwayTransition;
        const targetStates = this.getTargetStates();
        const adapter = SimpleListAdapter(sortArray(targetStates, state => state.name.toLowerCase()), ps => _jsx(_Fragment, { children: ps.item.name }));
        const selected = targetStates.find(ps => ps._id === pathwayTransition.item.targetId);
        return _jsxs(_Fragment, { children: [_jsxs("div", { className: 'pathway__form__label', children: ["Target State Name", _jsx("sup", { children: "*" })] }), _jsx(TS_DropDown, { adapter: adapter, selected: selected, caret: QuaiManager_DropDownCaret, onSelected: async (ps) => {
                        pathwayTransition.set('targetId', ps._id);
                        this.createDefaultLabel(pathwayTransition);
                        this.forceUpdate();
                        dispatch_PathwayStateRealtimeChanges.dispatchUI();
                    } })] });
    };
    renderLabelInput = () => {
        return _jsxs(_Fragment, { children: [_jsx("div", { className: 'pathway__form__label', children: "Label" }), _jsx(TS_Input, { type: 'text', value: this.state.pathwayTransition.item.label, onChange: (value) => {
                        this.state.pathwayTransition.set('label', value);
                        this.forceUpdate();
                    } })] });
    };
    renderAlertInput = () => {
        return _jsxs(_Fragment, { children: [_jsx("div", { className: 'pathway__form__label', children: "Alert" }), _jsx(TS_Input, { type: 'text', value: this.state.pathwayTransition.item.alert, onChange: (value) => {
                        this.state.pathwayTransition.set('alert', value);
                        this.forceUpdate();
                    } })] });
    };
    renderTests = () => {
        return _jsxs(_Fragment, { children: [_jsx("div", { className: 'pathway__form__label', children: "Tests" }), this.renderOrdersMultiSelect('testsIds', 'test')] });
    };
    renderTreatments = () => {
        return _jsxs(_Fragment, { children: [_jsx("div", { className: 'pathway__form__label', children: "Treatments" }), this.renderOrdersMultiSelect('treatmentsIds', 'treatment')] });
    };
    renderConsult = () => {
        return _jsxs(_Fragment, { children: [_jsx("div", { className: 'pathway__form__label', children: "Consult" }), this.renderOrdersMultiSelect('consultIds', 'consult')] });
    };
    childrenSelector = () => {
        const pathwayId = ModuleFE_PathwayEditor.getViewedPathwayId();
        if (!pathwayId)
            throw new MUSTNeverHappenException('cannot render this page without context of pathway id');
        const isParent = ModuleFE_Pathway.isParent(pathwayId);
        // If the pathway is not a parent or this state already exists
        if (!isParent || this.state.pathwayTransition.get('_id'))
            return;
        const currentTransition = this.state.pathwayTransition.item;
        return _jsx(Renderer_PathwayItemChildSelector, { onChangeCallback: pathwayIds => this.state.pathwayTransition
                .updateObj({ pathwayIds: filterDuplicates([pathwayId, ...pathwayIds]) }), pathwayId: pathwayId, childFilter: child => {
                const sourceState = ModuleFE_PathwayState.cache.unique(currentTransition.sourceId);
                if (!sourceState?.pathwayIds.includes(child._id))
                    return false;
                if (!currentTransition.targetId)
                    return true;
                const targetState = ModuleFE_PathwayState.cache.unique(currentTransition.targetId);
                return !!targetState?.pathwayIds.includes(child._id);
            } });
    };
    renderNurse = () => {
        return _jsxs(_Fragment, { children: [_jsx("div", { className: 'pathway__form__label', children: "Nurse" }), this.renderOrdersMultiSelect('nurseIds', 'poc')] });
    };
    renderMetapathwayActions = () => {
        const transition = this.state.pathwayTransition;
        return _jsxs(_Fragment, { children: [_jsx("div", { className: 'pathway__form__label', children: "Metapathway Actions" }), _jsx(TS_MultiSelect_V2, { editable: transition, prop: 'metaPathwayActions', selectionRenderer: this.renderMetapathwayActionSelector, itemRenderer: this.renderMetapathwayAction })] });
    };
    renderMetapathwayActionSelector = (selector) => {
        return _jsx(Dropdown_MetaPathwayAction_Type.selectable, { onSelected: async (type) => {
                const action = getCleanMetaPathwayAction(type);
                selector.onSelected(action);
                this.forceUpdate();
            }, queryFilter: type => {
                return !selector.existingItems.find(action => action.type === type);
            } });
    };
    renderMetapathwayAction = (action, onDelete) => {
        return _jsxs(LL_V_L, { className: 'pathway__transition__metapathway-action', children: [_jsxs(LL_H_C, { className: 'pathway__transition__metapathway-action__label', children: [metaPathwayAction_Type_LabelMap[action.type], _jsx(ICONS.x.component, { onClick: async () => {
                                await onDelete?.();
                                this.forceUpdate();
                            } })] }), isMetaPathwayAction_WithProtocol(action) && this.renderMetapathwayActionProtocols(action)] });
    };
    renderMetapathwayActionProtocols = (action) => {
        return _jsxs(_Fragment, { children: ["Protocols:", _jsxs(LL_V_L, { className: 'pathway__transition__metapathway-action__protocols', children: [action.protocolIds.map(id => {
                            const protocol = ModuleFE_Pathway.cache.unique(id);
                            if (!protocol)
                                throw new MUSTNeverHappenException(`Could not find pathway for id ${id}`);
                            return _jsxs(LL_H_C, { className: 'pathway__transition__metapathway-action__protocol', children: [protocol.label, _jsx(ICONS.x.component, { onClick: () => {
                                            removeItemFromArray(action.protocolIds, id);
                                            this.forceUpdate();
                                        } })] });
                        }), _jsx(DropDown_Pathway.selectable, { queryFilter: pathway => !action.protocolIds.includes(pathway._id), onSelected: pathway => {
                                action.protocolIds.push(pathway._id);
                                this.forceUpdate();
                            } })] })] });
    };
}
//# sourceMappingURL=Renderer_PathwayTransitionEditor.js.map